/**
 * Every icon the portal draws, and the two ways it draws them.
 *
 * The path data is Lucide at stroke-width 2.75 — the Organic guide's own weight — copied
 * character-for-character out of the approved prototype. A `d` string is not something to
 * paraphrase: a single wrong digit renders a shape that is subtly, silently wrong, and an empty
 * array renders an `<svg>` with nothing in it and no error at all. That is what happened to the
 * reminder rows and the "Coming up" cards in the prototype's first port attempt.
 *
 * TWO MECHANISMS, ON PURPOSE:
 *
 *  · The four NAVIGATION icons live in a <symbol> sprite and are used through <use>, because they
 *    appear in the sidebar AND the bottom bar. Defining each once is what lets a single NAV_ITEMS
 *    loop drive both navigations without the geometry being pasted twice.
 *  · The care and UI icons are inlined by <Icon>, because each appears once per row and a sprite
 *    would buy nothing.
 */

import type { CSSProperties, ReactElement, ReactNode } from 'react'
import type { CareItemKind } from '@ft/shared'

// ── Care icons (dc.html:786-793) ────────────────────────────────────────────────────────────────

export type CareIconId = 'bug' | 'droplet' | 'shield' | 'wind' | 'scissors' | 'bone' | 'phone'

/** 24 `d` strings: bug 11, droplet 1, shield 2, wind 3, scissors 5, bone 1, phone 1. */
export const CARE_ICON_PATHS: Readonly<Record<CareIconId, readonly string[]>> = {
  bug: [
    'm8 2 1.88 1.88',
    'M14.12 3.88 16 2',
    'M9 7.13v-1a3.003 3.003 0 1 1 6 0v1',
    'M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6',
    'M12 20v-9',
    'M6.53 9C4.6 8.8 3 7.1 3 5',
    'M6 13H2',
    'M3 21c0-2.1 1.7-3.8 3.8-4',
    'M20.97 5c0 2.1-1.6 3.8-3.5 4',
    'M22 13h-4',
    'M17.2 17c2.1.2 3.8 1.9 3.8 4',
  ],
  droplet: [
    'M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7z',
  ],
  shield: [
    'M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z',
    'm9 12 2 2 4-4',
  ],
  wind: ['M12.8 19.6A2 2 0 1 0 14 16H2', 'M17.5 8a2.5 2.5 0 1 1 2 4H2', 'M9.8 4.4A2 2 0 1 1 11 8H2'],
  scissors: [
    'M9 6a3 3 0 1 0-6 0a3 3 0 1 0 6 0',
    'M8.12 8.12 12 12',
    'M20 4 8.12 15.88',
    'M9 18a3 3 0 1 0-6 0a3 3 0 1 0 6 0',
    'M14.8 14.8 20 20',
  ],
  bone: [
    'M17 10c.7-.7 1.69 0 2.5 0a2.5 2.5 0 1 0 0-5 .5.5 0 0 1-.5-.5 2.5 2.5 0 1 0-5 0c0 .81.7 1.8 0 2.5l-7 7c-.7.7-1.69 0-2.5 0a2.5 2.5 0 0 0 0 5c.28 0 .5.22.5.5a2.5 2.5 0 1 0 5 0c0-.81-.7-1.8 0-2.5Z',
  ],
  phone: [
    'M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z',
  ],
}

/**
 * `CareItem.kind` → its glyph. Exhaustive by type: add a kind to the shared contract and this
 * stops compiling, which is the point.
 *
 * `scissors` is deliberately unmapped. It drew "Grooming" in the prototype, an item backed by no
 * field at all and rendered at an invented 64%. The shared care schedule dropped it rather than
 * draw a confident ring over nothing; the glyph is kept because grooming is a real service and
 * will come back once a field exists behind it.
 */
const CARE_ICON_FOR: Readonly<Record<CareItemKind, CareIconId>> = {
  worming: 'bug',
  flea: 'droplet',
  booster: 'shield',
  kennel: 'wind',
  feeding: 'bone',
  contact: 'phone',
}

export function careIconFor(kind: CareItemKind): readonly string[] {
  return CARE_ICON_PATHS[CARE_ICON_FOR[kind]]
}

// ── UI icons ────────────────────────────────────────────────────────────────────────────────────

export type UiIconId = 'arrowRight' | 'check' | 'close' | 'chevronRight' | 'message'

/**
 * The single-geometry UI glyphs, so five screens do not each retype them.
 *
 * NOT here, because they mix `<circle>`/`<rect>` with `<path>` and `<Icon>` only draws paths:
 * search (dc.html:180, 205), smartphone (264) and mail (268). Copy those three inline from those
 * exact lines if you need them — do not reconstruct them from memory.
 */
export const UI_ICON_PATHS: Readonly<Record<UiIconId, readonly string[]>> = {
  arrowRight: ['M5 12h14', 'm12 5 7 7-7 7'],
  check: ['M20 6 9 17l-5-5'],
  close: ['M18 6 6 18', 'm6 6 12 12'],
  chevronRight: ['m9 18 6-6-6-6'],
  message: ['M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z'],
}

// ── <Icon> ──────────────────────────────────────────────────────────────────────────────────────

export interface IconProps {
  /** From `CARE_ICON_PATHS`, `UI_ICON_PATHS` or `careIconFor()`. */
  readonly paths: readonly string[]
  /** Rendered px. The prototype uses 14, 15, 16, 17, 18, 19, 20, 22, 24 and 28 at various sites. */
  readonly size?: number
  /** Defaults to `currentColor`, which is how every icon in the prototype takes its colour. */
  readonly stroke?: string
  readonly className?: string
  readonly style?: CSSProperties
  /**
   * Supply this ONLY when the icon is the sole carrier of meaning. Every icon in the prototype sits
   * beside its own text label, so the right answer is almost always to leave it out and let the
   * icon be hidden from assistive tech — a second announcement of "bug" next to "Worming" is noise.
   */
  readonly title?: string
}

/**
 * One inline Lucide glyph.
 *
 * stroke-width 2.75 is the Organic guide's weight and is not a per-icon choice; it is what makes
 * the set read as one family. In JSX the SVG presentation attributes must be camelCased
 * (`strokeWidth`, not `stroke-width`) — but `viewBox` keeps its capital B.
 */
export function Icon({
  paths,
  size = 20,
  stroke = 'currentColor',
  className,
  style,
  title,
}: IconProps): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={stroke}
      strokeWidth="2.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
      {...(title === undefined
        ? { 'aria-hidden': true, focusable: false }
        : { role: 'img', focusable: false })}
    >
      {title === undefined ? null : <title>{title}</title>}
      {paths.map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  )
}

// ── Navigation sprite ───────────────────────────────────────────────────────────────────────────

export type NavIconId = 'home' | 'paw' | 'bell' | 'clipboard'

const NAV_SYMBOL_ID: Readonly<Record<NavIconId, string>> = {
  home: 'ft-ic-home',
  paw: 'ft-ic-paw',
  bell: 'ft-ic-bell',
  clipboard: 'ft-ic-clipboard',
}

/**
 * The `<use href>` target for a nav icon.
 *
 * A bare `#id` fragment. Never introduce a `<base href>` or a router basename that rewrites
 * fragments — both navigations go blank and nothing reports it.
 */
export function spriteHref(icon: NavIconId): string {
  return `#${NAV_SYMBOL_ID[icon]}`
}

/** The geometry, once — shared by the sprite and by the sidebar's inline brand mark. */
function navIconShapes(icon: NavIconId): ReactNode {
  switch (icon) {
    case 'home':
      return (
        <>
          <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8" />
          <path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        </>
      )
    case 'paw':
      return (
        <>
          <circle cx="11" cy="4" r="2" />
          <circle cx="18" cy="8" r="2" />
          <circle cx="20" cy="16" r="2" />
          <path d="M9 10a5 5 0 0 1 5 5v3.5a3.5 3.5 0 0 1-6.84 1.045Q6.52 17.48 4.46 16.84A3.5 3.5 0 0 1 5.5 10Z" />
        </>
      )
    case 'bell':
      return (
        <>
          <path d="M10.268 21a2 2 0 0 0 3.464 0" />
          <path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" />
        </>
      )
    case 'clipboard':
      return (
        <>
          <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
          <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
          <path d="m9 14 2 2 4-4" />
        </>
      )
  }
}

/**
 * The sprite. Mount it ONCE, at the top of the app shell, above both navigations.
 *
 * Hidden by clipping it to nothing rather than with `display:none` — some engines treat a
 * `display:none` subtree as a reason not to resolve `<use>`, and the failure is a pair of empty
 * navigations with no console message.
 */
export function IconSprite(): ReactElement {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      style={{ position: 'absolute', width: '0', height: '0', overflow: 'hidden' }}
      width="0"
      height="0"
    >
      {(Object.keys(NAV_SYMBOL_ID) as NavIconId[]).map((icon) => (
        <symbol
          key={icon}
          id={NAV_SYMBOL_ID[icon]}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {navIconShapes(icon)}
        </symbol>
      ))}
    </svg>
  )
}

export interface SpriteIconProps {
  readonly icon: NavIconId
  /** 20 in both navigations. */
  readonly size?: number
}

/**
 * A nav icon, drawn from the sprite.
 *
 * There is deliberately NO `viewBox` on this wrapper: it inherits scale from the `<symbol>`.
 * Adding one rescales both navigations at once.
 */
export function SpriteIcon({ icon, size = 20 }: SpriteIconProps): ReactElement {
  return (
    <svg width={size} height={size} aria-hidden="true" focusable="false">
      {/* `href`, not the deprecated `xlinkHref` — React 18 renders the modern attribute. */}
      <use href={spriteHref(icon)} />
    </svg>
  )
}

/**
 * The sidebar's brand mark: the same paw geometry at 22px, inlined rather than referenced.
 *
 * It is a real drawn mark sitting in a filled circle, not a navigation affordance, which is why it
 * does not go through the sprite.
 */
export function BrandPaw({ size = 22 }: { readonly size?: number }): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {navIconShapes('paw')}
    </svg>
  )
}
